import { useMutation, useQuery } from 'react-query';
import type { UseMutationOptions, UseQueryOptions } from 'react-query';
import { httpRequest } from '../config/api';
import { QUERY_KEYS } from './constants';
import type {
  SchemaCommentMessageCreateSchema as MessageCreateSchema,
  SchemaCommentMessageResponseSchema as MessageResponseSchema,
  SchemaCommentThreadCreateSchema as ThreadCreateSchema,
  SchemaCommentThreadResponseSchema as ThreadResponseSchema,
  operations
} from '../api/generated/schema';

type ListThreadsParams = operations['list_threads_api_threads_get']['parameters']['query'];

export type CommentMessagePayload = MessageCreateSchema & Partial<ThreadCreateSchema>;

type AllCommentsQueryOptions = UseQueryOptions<
  ThreadResponseSchema[],
  unknown,
  ThreadResponseSchema[],
  [string, string, ListThreadsParams | undefined]
>;

export const COMMENT_SERVICE = {
  getAllComments: (params?: ListThreadsParams) =>
    httpRequest.get('api/threads', {
      params
    }) as Promise<ThreadResponseSchema[]>,
  addCommentToThread: ({ threadID, data }: { threadID: string; data: CommentMessagePayload }) =>
    httpRequest.post(`api/threads/${threadID}/messages`, data) as Promise<MessageResponseSchema>,
  editComment: ({
    threadID,
    commentID,
    data
  }: {
    threadID: string;
    commentID: string;
    data: MessageCreateSchema;
  }) =>
    httpRequest.put(
      `api/threads/${threadID}/messages/${commentID}`,
      data
    ) as Promise<MessageResponseSchema>,
  createThread: ({ data }: { data: ThreadCreateSchema }) =>
    httpRequest.post(`api/threads`, data) as Promise<ThreadResponseSchema>,
  deleteComment: ({ threadID, commentID }: { threadID: string; commentID: string }) =>
    httpRequest.delete(`api/threads/${threadID}/messages/${commentID}`) as Promise<void>,
  deleteThread: ({ threadID }: { threadID: string }) =>
    httpRequest.delete(`api/threads/${threadID}`) as Promise<void>,
  resolveThread: ({ threadID }: { threadID: string }) =>
    httpRequest.post(`api/threads/${threadID}/resolve`) as Promise<ThreadResponseSchema>
};

export const useAllComments = ({
  queryOptions,
  customKey = '',
  params
}: {
  queryOptions?: AllCommentsQueryOptions;
  customKey?: string;
  params?: ListThreadsParams;
} = {}) => {
  return useQuery<ThreadResponseSchema[]>(
    [QUERY_KEYS.GET_ALL_COMMENTS, customKey, params],
    () => COMMENT_SERVICE.getAllComments(params),
    queryOptions
  );
};

export const useAddCommentToThread = ({
  mutationOptions
}: {
  mutationOptions?: UseMutationOptions<
    MessageResponseSchema,
    unknown,
    { threadID: string; data: CommentMessagePayload }
  >;
} = {}) => {
  return useMutation(COMMENT_SERVICE.addCommentToThread, mutationOptions);
};

export const useEditComment = ({
  mutationOptions
}: {
  mutationOptions?: UseMutationOptions<
    MessageResponseSchema,
    unknown,
    { threadID: string; commentID: string; data: MessageCreateSchema }
  >;
} = {}) => {
  return useMutation(COMMENT_SERVICE.editComment, mutationOptions);
};

export const useCreateThread = ({
  mutationOptions
}: {
  mutationOptions?: UseMutationOptions<ThreadResponseSchema, unknown, { data: ThreadCreateSchema }>;
} = {}) => {
  return useMutation(COMMENT_SERVICE.createThread, mutationOptions);
};

export const useDeleteCommentInThread = ({
  mutationOptions
}: {
  mutationOptions?: UseMutationOptions<void, unknown, { threadID: string; commentID: string }>;
} = {}) => {
  return useMutation(COMMENT_SERVICE.deleteComment, mutationOptions);
};

export const useResolveThread = ({
  mutationOptions
}: {
  mutationOptions?: UseMutationOptions<ThreadResponseSchema, unknown, { threadID: string }>;
} = {}) => {
  return useMutation(COMMENT_SERVICE.resolveThread, mutationOptions);
};

export const useDeleteThread = ({
  mutationOptions
}: {
  mutationOptions?: UseMutationOptions<void, unknown, { threadID: string }>;
} = {}) => {
  return useMutation(COMMENT_SERVICE.deleteThread, mutationOptions);
};
